import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ShieldCheck, ExternalLink, ArrowLeft, Save, History, LayoutList, KeyRound, Copy, RefreshCw, Ban } from "lucide-react";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import {
  fetchAdminSite, patchAdminSite, changeSiteStatus, fetchSiteHistory, fetchAdminSiteCapabilities, patchAdminSiteCapabilities,
  fetchWebsiteClaimCode, revealWebsiteClaimCode, regenerateWebsiteClaimCode, revokeWebsiteClaimCode,
} from "@/lib/api";
import { PLANS, PLAN_ADD_ONS, getPlanPrice } from "@/data/content";

const CAPABILITY_LABELS = [
  ["appointments", "Appointments"], ["customers", "Customers"], ["leads", "Leads"],
  ["analytics", "Analytics"], ["inventory", "Inventory"], ["commerce", "Commerce"],
  ["orders", "Orders"], ["shipping", "Shipping"], ["business_reminders", "Business Reminders"],
  ["tax_calendar", "Tax Calendar"], ["billing", "Billing"],
];

function SiteCapabilitiesPanel({ token, slug }) {
  const [caps, setCaps] = useState(null);
  const load = () => fetchAdminSiteCapabilities(token, slug).then(setCaps).catch(() => {});
  // A bare `useEffect(load, deps)` passes load's own return value (a Promise, since
  // its body is a .then()/.catch() chain) as the effect's return — React reads that
  // as an attempted cleanup function and throws ("destroy is not a function"),
  // crashing this entire page. Wrapping in a plain (non-async, void-returning)
  // arrow function is the fix — a real bug found live while adding
  // WebsiteAccessPanel below, not merely a latent style issue.
  useEffect(() => { load(); }, [token, slug]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!caps) return null;
  const toggle = (key) => patchAdminSiteCapabilities(token, slug, { [key]: !caps[key] }).then(setCaps);

  return (
    <div className="mt-8 rounded-xl border border-slate-200 bg-white p-6" data-testid="site-capabilities-panel">
      <h2 className="flex items-center gap-2 font-semibold mb-1"><LayoutList size={17} className="text-emerald-600" /> Business Modules</h2>
      <p className="text-xs text-slate-500 mb-4">
        {caps._configured ? "Explicitly configured for this website." : "Showing category defaults — nothing has been explicitly saved yet."}
      </p>
      <div className="grid sm:grid-cols-2 gap-2">
        {CAPABILITY_LABELS.map(([key, label]) => (
          <label key={key} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2 text-sm">
            {label}
            <input type="checkbox" checked={!!caps[key]} onChange={() => toggle(key)} className="h-4 w-4" />
          </label>
        ))}
      </div>
    </div>
  );
}

// Website Access (claim) Code — "how does MySiteCare know which website belongs to
// which customer?" The admin reveals/copies a one-time code here and sends it to the
// real business owner; entering it once (as an authenticated customer, see
// CustomerAddWebsite.js) links that exact website to their account. See
// claim_code_routes.py's module docstring for the full architecture.
function WebsiteAccessPanel({ token, slug }) {
  const [claim, setClaim] = useState(null);
  const [revealedCode, setRevealedCode] = useState(null);
  const [copied, setCopied] = useState(false);
  const [confirmingRegenerate, setConfirmingRegenerate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = () => fetchWebsiteClaimCode(token, slug).then((c) => { setClaim(c); setRevealedCode(null); }).catch(() => {});
  useEffect(() => { load(); }, [token, slug]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!claim) return null;

  const reveal = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await revealWebsiteClaimCode(token, slug);
      setRevealedCode(res.code);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not reveal the code.");
    } finally {
      setBusy(false);
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard permission denied — Reveal still shows the code to copy by hand */ }
  };

  const regenerate = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await regenerateWebsiteClaimCode(token, slug);
      setRevealedCode(res.code);
      setClaim({ ...claim, status: res.status, version: res.version });
      setConfirmingRegenerate(false);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not generate a new code.");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    setBusy(true);
    setError("");
    try {
      await revokeWebsiteClaimCode(token, slug);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Could not revoke the code.");
    } finally {
      setBusy(false);
    }
  };

  const invitationText = revealedCode
    ? `Your MySiteCare website is ready.\n\nCreate or sign in to your MySiteCare account and enter this Website Access Code:\n\n${revealedCode}\n\nThis code can only be used once.`
    : null;

  return (
    <div className="mt-8 rounded-xl border border-slate-200 bg-white p-6" data-testid="website-access-panel">
      <h2 className="flex items-center gap-2 font-semibold mb-1"><KeyRound size={17} className="text-emerald-600" /> Customer Access</h2>
      <p className="text-xs text-slate-500 mb-4">Send this code to the actual business owner. It identifies this exact website at checkout and is consumed only after verified payment.</p>

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Status</div>
          <div className="text-sm font-semibold mt-0.5">
            {claim.status === "active" && "Unclaimed"}
            {claim.status === "claimed" && `Claimed${claim.claimed_by ? ` — ${claim.claimed_by}` : ""}`}
            {claim.status === "revoked" && "Revoked"}
          </div>
          {claim.checkout_status === "pending" && <p className="text-xs text-amber-700">Payment checkout pending</p>}
        </div>
        {claim.status === "active" && (
          <div className="font-mono text-lg tracking-wider" data-testid="claim-code-display">
            {revealedCode || "•••• •••• ••••"}
          </div>
        )}
      </div>

      {error && <p className="text-sm text-rose-600 mt-3" role="alert">{error}</p>}

      <div className="flex flex-wrap gap-3 mt-4">
        {claim.status === "active" && !revealedCode && (
          <button type="button" onClick={reveal} disabled={busy} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold hover:bg-slate-100 disabled:opacity-50">
            Reveal
          </button>
        )}
        {claim.status === "active" && revealedCode && (
          <>
            <button type="button" onClick={() => copy(revealedCode)} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold hover:bg-slate-100">
              <Copy size={14} /> {copied ? "Copied!" : "Copy Code"}
            </button>
            <button type="button" onClick={() => copy(invitationText)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold hover:bg-slate-100">
              Copy Invitation
            </button>
          </>
        )}
        {claim.status && (
          <button type="button" onClick={() => setConfirmingRegenerate(true)} disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold hover:bg-slate-100 disabled:opacity-50">
            <RefreshCw size={14} /> Generate New Code
          </button>
        )}
        {claim.status === "active" && (
          <button type="button" onClick={revoke} disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg border border-rose-300 text-rose-700 px-4 py-2 text-sm font-semibold hover:bg-rose-50 disabled:opacity-50">
            <Ban size={14} /> Revoke
          </button>
        )}
      </div>

      {confirmingRegenerate && (
        <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-4">
          <p className="text-sm text-slate-800">The previous code will immediately stop working. Continue?</p>
          <div className="flex gap-3 mt-3">
            <button type="button" onClick={regenerate} disabled={busy} className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Generate New Code</button>
            <button type="button" onClick={() => setConfirmingRegenerate(false)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold hover:bg-slate-100">Cancel</button>
          </div>
        </div>
      )}

      <div className="text-xs text-slate-500 mt-4">Created {new Date(claim.created_at).toLocaleDateString()}</div>
    </div>
  );
}

const STATUS_LABEL = {
  offline_concept: "Offline Concept",
  proposal_accepted: "Proposal Accepted",
  online_client: "Online Client",
  rejected: "Rejected",
};

const REJECTION_REASONS = [
  ["not_interested", "Not interested"],
  ["price", "Price"],
  ["already_has_provider", "Already has a provider"],
  ["does_not_want_subscription", "Doesn't want a subscription"],
  ["does_not_want_website", "Doesn't want a website"],
  ["timing", "Timing"],
  ["unable_to_reach_decision_maker", "Unable to reach decision-maker"],
  ["other", "Other"],
];

// Plan labels come from the same PLANS/getPlanPrice source of truth the public /ph
// pricing pages use (data/content.js) — not duplicated here.
const PLAN_OPTIONS = PLANS.map((p) => {
  const ph = getPlanPrice(p.id, "ph");
  return `${p.name} — ${ph.price}${ph.period === "one-time" ? " one-time" : ph.period}`;
}).concat(
  // Assisted Setup & Launch is a Website Ownership add-on, not a browsable plan (see
  // PLAN_ADD_ONS in data/content.js) — still listed here, distinctly labeled, so sales
  // can record it as what was actually recommended/sold to a lead.
  PLAN_ADD_ONS.map((p) => {
    const ph = getPlanPrice(p.id, "ph");
    return `${p.name} — ${ph.price} one-time (Website Ownership add-on)`;
  }),
);

const TRANSITION_COPY = {
  proposal_accepted: {
    label: "Mark Proposal Accepted",
    confirm: "Mark proposal as accepted? This records that the business has agreed to proceed. The website will remain in concept mode until it is deliberately marked Online.",
  },
  online_client: {
    label: "Mark Online",
    confirm: "Mark this website as an Online Client? This indicates the website is now an official client website. Confirm business details and go-live requirements have been verified.",
  },
  rejected: {
    label: "Mark Rejected",
    confirm: "Mark this prospect as rejected? Research and the concept site are kept — this only stops normal outreach.",
  },
  offline_concept: {
    label: "Return to Concept",
    confirm: "Return this website to Offline Concept status?",
  },
};

const ALLOWED_TRANSITIONS = {
  offline_concept: ["proposal_accepted", "rejected"],
  proposal_accepted: ["online_client", "rejected", "offline_concept"],
  online_client: ["proposal_accepted"],
  rejected: ["offline_concept"],
};

function Field({ label, value, onChange, placeholder, type = "text" }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-slate-600 mb-1">{label}</span>
      <input type={type} value={value || ""} placeholder={placeholder || "Not recorded"} onChange={(e) => onChange(e.target.value)} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm" />
    </label>
  );
}

export default function AdminSiteDetail() {
  const { slug } = useParams();
  const { token, email, checking, isLoggedIn, revealAdminUi } = useAdminAuth();
  const [site, setSite] = useState(null);
  const [form, setForm] = useState(null);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [pendingTransition, setPendingTransition] = useState(null);
  const [transitionNote, setTransitionNote] = useState("");
  const [rejectionReason, setRejectionReason] = useState("other");

  const load = () => {
    if (!token) return;
    fetchAdminSite(token, slug).then((s) => { setSite(s); setForm(s); }).catch((err) => setError(err.response?.data?.detail || "Could not load this site."));
    fetchSiteHistory(token, slug).then(setHistory).catch(() => {});
  };

  useEffect(load, [token, slug]); // eslint-disable-line react-hooks/exhaustive-deps

  if (checking) return null;
  if (!revealAdminUi) return <Navigate to="/admin/login" replace />;
  if (error) return <div className="min-h-screen bg-slate-50 px-4 py-16 text-center text-rose-600">{error}</div>;
  if (!isLoggedIn) return <div className="min-h-screen bg-slate-50 px-4 py-16 text-center text-slate-500">DEBUG — Admin UI revealed, but this page needs a real login to load real data.</div>;
  if (!site || !form) return null;

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  const save = async () => {
    setSaving(true);
    setMessage("");
    try {
      const editable = ["location", "notes", "selected_plan", "recommended_plan", "sales_priority", "discoverability_tier",
        "official_url", "facebook_url", "messenger_url", "instagram_url", "linkedin_url", "existing_website_url",
        "phone", "email", "rating", "review_count", "why_page_enabled"];
      const updates = {};
      editable.forEach((k) => { if (form[k] !== site[k]) updates[k] = form[k]; });
      if (Object.keys(updates).length === 0) { setMessage("Nothing changed."); setSaving(false); return; }
      const updated = await patchAdminSite(token, slug, updates);
      setSite(updated);
      setForm(updated);
      setMessage("Saved.");
    } catch (err) {
      setMessage(err.response?.data?.detail || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const confirmTransition = async () => {
    setSaving(true);
    try {
      const updated = await changeSiteStatus(token, slug, pendingTransition, transitionNote || undefined, pendingTransition === "rejected" ? rejectionReason : undefined);
      setSite(updated);
      setForm(updated);
      setPendingTransition(null);
      setTransitionNote("");
      load();
    } catch (err) {
      setMessage(err.response?.data?.detail || "Could not change status.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-16 text-slate-900">
      <div className="mx-auto max-w-4xl">
        <Link to="/admin/sites" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800"><ArrowLeft size={14} /> Back to Website Management</Link>

        <div className="mt-4 flex items-center gap-2.5 text-emerald-700"><ShieldCheck size={20} /><span className="text-xs font-bold uppercase tracking-[0.16em]">Logged in as {email}</span></div>
        <h1 className="mt-2 font-display text-3xl font-bold">{site.business_name}</h1>
        <p className="text-sm text-slate-500">{site.category} · {STATUS_LABEL[site.status]}</p>

        <div className="mt-6 flex flex-wrap gap-3">
          <a href={site.concept_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold hover:bg-slate-100"><ExternalLink size={14} /> Open Concept</a>
          {site.official_url && <a href={site.official_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-800 hover:bg-emerald-100"><ExternalLink size={14} /> Open Official Website</a>}
          {site.why_page_enabled && <a href={`https://mysitecare.eu/ph/why-this-matters?biz=${site.slug}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold hover:bg-slate-100"><ExternalLink size={14} /> Preview Why Page</a>}
          <Link to={`/admin/sites/${site.slug}/onboarding`} className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-300 bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-800 hover:bg-indigo-100">Client Onboarding &amp; Go Live</Link>
        </div>

        {/* STATUS ACTIONS */}
        <div className="mt-8 rounded-xl border border-slate-200 bg-white p-6">
          <h2 className="font-semibold mb-4">Website Status: <span className="font-normal">{STATUS_LABEL[site.status]}</span></h2>
          <div className="flex flex-wrap gap-3">
            {(ALLOWED_TRANSITIONS[site.status] || []).map((next) => (
              <button key={next} type="button" onClick={() => setPendingTransition(next)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold hover:bg-slate-100">
                {TRANSITION_COPY[next].label}
              </button>
            ))}
          </div>
          {site.rejected_at && site.status === "rejected" && (
            <p className="mt-4 text-sm text-slate-600">Rejected on {new Date(site.rejected_at).toLocaleDateString()}. Reason: {site.rejection_reason || "—"}</p>
          )}
        </div>

        {site.status === "online_client" && <SiteCapabilitiesPanel token={token} slug={site.slug} />}
        {site.status === "online_client" && <WebsiteAccessPanel token={token} slug={site.slug} />}

        {pendingTransition && (
          <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-6">
            <p className="text-sm text-slate-800">{TRANSITION_COPY[pendingTransition].confirm}</p>
            {pendingTransition === "rejected" && (
              <select value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} className="mt-3 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm">
                {REJECTION_REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            )}
            <textarea value={transitionNote} onChange={(e) => setTransitionNote(e.target.value)} placeholder="Optional note for the audit trail" className="mt-3 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm" rows={2} />
            <div className="mt-3 flex gap-3">
              <button type="button" disabled={saving} onClick={confirmTransition} className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-bold text-white disabled:opacity-50">Confirm</button>
              <button type="button" onClick={() => setPendingTransition(null)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold">Cancel</button>
            </div>
          </div>
        )}

        {/* OVERVIEW + PLAN */}
        <div className="mt-8 rounded-xl border border-slate-200 bg-white p-6 grid sm:grid-cols-2 gap-4">
          <Field label="Location" value={form.location} onChange={set("location")} />
          <label className="block">
            <span className="block text-xs font-semibold text-slate-600 mb-1">Sales priority</span>
            <select value={form.sales_priority || ""} onChange={(e) => set("sales_priority")(e.target.value)} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm">
              <option value="">Not set</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </label>
          <label className="block">
            <span className="block text-xs font-semibold text-slate-600 mb-1">Recommended plan</span>
            <select value={form.recommended_plan || ""} onChange={(e) => set("recommended_plan")(e.target.value)} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm">
              <option value="">Not set</option>
              {PLAN_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="block text-xs font-semibold text-slate-600 mb-1">Selected plan</span>
            <select value={form.selected_plan || ""} onChange={(e) => set("selected_plan")(e.target.value)} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm">
              <option value="">Not set</option>
              {PLAN_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </label>
          <Field label="Official URL / domain" value={form.official_url} onChange={set("official_url")} />
          <Field label="Discoverability tier" value={form.discoverability_tier} onChange={set("discoverability_tier")} />
        </div>

        {/* DIGITAL PRESENCE */}
        <div className="mt-6 rounded-xl border border-slate-200 bg-white p-6">
          <h2 className="font-semibold mb-4">Digital Presence</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Facebook" value={form.facebook_url} onChange={set("facebook_url")} />
            <Field label="Messenger" value={form.messenger_url} onChange={set("messenger_url")} />
            <Field label="Instagram" value={form.instagram_url} onChange={set("instagram_url")} />
            <Field label="LinkedIn" value={form.linkedin_url} onChange={set("linkedin_url")} />
            <Field label="Existing website" value={form.existing_website_url} onChange={set("existing_website_url")} />
            <Field label="Phone" value={form.phone} onChange={set("phone")} />
            <Field label="Email" value={form.email} onChange={set("email")} />
          </div>
        </div>

        {/* REPUTATION */}
        <div className="mt-6 rounded-xl border border-slate-200 bg-white p-6">
          <h2 className="font-semibold mb-4">Reputation</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Public rating" value={form.rating} onChange={set("rating")} placeholder="e.g. 4.9" />
            <Field label="Review count" type="number" value={form.review_count} onChange={(v) => set("review_count")(v === "" ? null : Number(v))} />
          </div>
        </div>

        {/* WHY PAGE */}
        <div className="mt-6 rounded-xl border border-slate-200 bg-white p-6">
          <h2 className="font-semibold mb-3">Why This Matters Page</h2>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={Boolean(form.why_page_enabled)} onChange={(e) => set("why_page_enabled")(e.target.checked)} />
            Personalized Why-page data exists for this business
          </label>
        </div>

        {/* NOTES */}
        <div className="mt-6 rounded-xl border border-slate-200 bg-white p-6">
          <h2 className="font-semibold mb-3">Internal Notes</h2>
          <textarea value={form.notes || ""} onChange={(e) => set("notes")(e.target.value)} rows={5} placeholder="Admin-only — never shown publicly." className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm" />
        </div>

        <div className="mt-6 flex items-center gap-3">
          <button type="button" disabled={saving} onClick={save} className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white disabled:opacity-50"><Save size={15} /> Save Changes</button>
          {message && <p className="text-sm text-slate-600">{message}</p>}
        </div>

        {/* HISTORY */}
        <div className="mt-10 rounded-xl border border-slate-200 bg-white p-6">
          <h2 className="font-semibold mb-4 flex items-center gap-2"><History size={16} /> Status History</h2>
          {history.length === 0 && <p className="text-sm text-slate-500">No status changes recorded yet.</p>}
          <ul className="space-y-3">
            {history.map((h) => (
              <li key={h.id} className="text-sm border-b border-slate-100 pb-3 last:border-0">
                <p className="font-medium">{STATUS_LABEL[h.previous_status]} → {STATUS_LABEL[h.new_status]}</p>
                <p className="text-xs text-slate-500">{new Date(h.at).toLocaleString()} · Changed by {h.admin_email}</p>
                {h.note && <p className="text-xs text-slate-600 mt-1">{h.note}</p>}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
